import {
    ChampionshipReignMatchesHash, ChampionshipReigns,
    Championships,
    Events,
    FeudMatchesHash,
    Feuds, GimmickMatches,
    MatchesHash,
    Superstars,
    TagTeams,
    Tournaments
} from "./models.js";
import {getTagTeamSuperstars} from "./tagTeam.js";

function Match(order, type = 'singles') {
    // String
    const objectToClaim = null;
    const location = null;
    const winner = null;
    const loser = null;
    const championshipsWon = null;
    const championshipsDefended = null;
    const championshipsRetained = null;
    const generatedText = null;
    const activeEffect = null;
    const finishName = null;
    const finishText = null;
    const objectName = 'match';

    // Integer
    const hype = 0;
    const streakBonus = 0;
    const eventBonus = 0;
    const contractBonus = 0;
    const tournamentFinalBonus = 0;
    const tournamentRound = null;
    const tournamentGroup = null;
    const refereeId = null;

    // Float
    const rating = null;

    // Boolean
    const hot = false;
    const hidden = null;

    // Array
    const fallGimmickNames = [];

    // Object
    const managersUsed = {};

    // Foreign key
    const eventId = null;
    const gimmickMatchId = null;
    const tournamentId = null;
    const championshipReignDefenseId = null;

    return {
        order,
        type,
        objectToClaim,
        location,
        winner,
        loser,
        championshipsWon,
        championshipsDefended,
        championshipsRetained,
        generatedText,
        activeEffect,
        finishName,
        finishText,
        hype,
        streakBonus,
        eventBonus,
        contractBonus,
        tournamentFinalBonus,
        tournamentRound,
        tournamentGroup,
        refereeId,
        rating,
        hot,
        hidden,
        fallGimmickNames,
        managersUsed,
        eventId,
        gimmickMatchId,
        tournamentId,
        championshipReignDefenseId,
        objectName,
    };
}

function addMatchSuperstars(match, superstars) {
    let matchHashes = superstars.map(x => {
        return {matchId: match.$loki, superstarId: x.$loki}
    });
    return MatchesHash.insert(matchHashes);
}

function addMatchTagTeams(match, tagTeams) {
    let matchHashes = tagTeams.map(x => {
        return {matchId: match.$loki, tagTeamId: x.$loki}
    });
    return MatchesHash.insert(matchHashes);
}

async function removeMatchParticipants(match) {  // todo test
    MatchesHash.findAndRemove({'matchId': match.$loki});
}

async function removeMatchParticipant(match, participant) {  // todo test
    if (participant.objectName === 'superstar') {
        MatchesHash.findAndRemove({'matchId': match.$loki, 'superstarId': participant.$loki});
    }
    else if (participant.objectName === 'tagTeam') {
        MatchesHash.findAndRemove({'matchId': match.$loki, 'tagTeamId': participant.$loki});
    }
}

function addMatchChampionships(match, championships) {
    championships.forEach(c => {
        c.matchId = match.$loki
    });
}

function removeMatchChampionships(match, championships) {
    championships.forEach(c => {
        c.matchId = null
    });
}

function getMatchParticipants(match) {
    if (match.type === 'singles') {
        return getMatchSuperstars(match)
    }
    else {
        return getMatchTagTeams(match)
    }
}

async function getMatchSuperstars(match) {
    const matchHashes = await MatchesHash.find({'matchId': match.$loki});
    const superstarIds = matchHashes.map(x => {
        return x.superstarId
    });
    const result = Superstars.find({'$loki': {'$in': superstarIds}});
    result.sort((a, b) => superstarIds.indexOf(a.$loki) - superstarIds.indexOf(b.$loki));
    return result;
}

async function getMatchTagTeams(match) {
    const matchHashes = await MatchesHash.find({'matchId': match.$loki});
    const tagTeamIds = matchHashes.map(x => {
        return x.tagTeamId
    });
    const result = TagTeams.find({'$loki': {'$in': tagTeamIds}});
    result.sort((a, b) => tagTeamIds.indexOf(a.$loki) - tagTeamIds.indexOf(b.$loki));
    return result;
}

async function getAllSuperstarsForMatch(match) {
    if (match.type === 'singles') {
        return getMatchSuperstars(match)
    }
    else {
        let allSuperstars = [];
        for await (const x of await getMatchTagTeams(match)) {
            allSuperstars.push(...await getTagTeamSuperstars(x));
        }
        return allSuperstars;
    }
}

function getMatchChampionships(match) {
    return Championships.find({'matchId': match.$loki});
}

function getMatchEvent(match) {
    if (!match.eventId) {
        return
    }
    return Events.find({'$loki': match.eventId})[0];
}

function getMatchGimmickMatch(match) {
    if (!match.gimmickMatchId) {
        return
    }
    return GimmickMatches.find({'$loki': match.gimmickMatchId})[0];
}

function addMatchChampionshipReigns(match, championshipReigns) {
    let matchHashes = championshipReigns.map(x => {
        return {matchId: match.$loki, championshipReignId: x.$loki}
    });
    return ChampionshipReignMatchesHash.insert(matchHashes);
}

async function getMatchChampionshipReigns(match) {
    const matchHashes = await ChampionshipReignMatchesHash.find({'matchId': match.$loki});
    const championshipReignIds = matchHashes.map(x => {
        return x.championshipReignId
    });
    return ChampionshipReigns.find({'$loki': {'$in': championshipReignIds}});
}

function getMatchTournament(match) {
    if (!match.tournamentId) {
        return
    }
    return Tournaments.find({'$loki': match.tournamentId})[0];
}

function addMatchFeuds(match, feuds) {
    let matchHashes = feuds.map(x => {
        return {matchId: match.$loki, feudId: x.$loki}
    });
    return FeudMatchesHash.insert(matchHashes);
}

async function removeMatchFeud(match, feud) {
    FeudMatchesHash.findAndRemove({'matchId': match.$loki, 'feudId': feud.$loki});
}

async function getMatchFeuds(match) {
    const matchHashes = await FeudMatchesHash.find({'matchId': match.$loki});
    const feudIds = matchHashes.map(x => {
        return x.feudId
    });
    return Feuds.find({'$loki': {'$in': feudIds}});
}

async function addMatchParticipants(match, participants) {
    for (const p of participants) {
        if (p.objectName === 'superstar') {
            await addMatchSuperstars(match, [p])
        }
        else if (p.objectName === 'tagTeam') {
            await addMatchTagTeams(match, [p])
        }
    }
}

export { Match, getMatchParticipants, addMatchSuperstars, addMatchTagTeams, addMatchChampionships, getMatchSuperstars,
    getMatchChampionships, getAllSuperstarsForMatch, getMatchEvent, addMatchChampionshipReigns, getMatchChampionshipReigns,
    getMatchGimmickMatch, getMatchTournament, addMatchFeuds, getMatchFeuds, removeMatchParticipants, addMatchParticipants,
    removeMatchChampionships, removeMatchFeud, removeMatchParticipant, getMatchTagTeams }